import { CommonModule } from "@angular/common";
import { Component, computed, input, numberAttribute, ViewEncapsulation } from "@angular/core";
import { CalendarData } from "../../modules/home/calendar/CalendarData";
import { Proto } from "../../pojo/Proto";
import { S25Util } from "../../util/s25-util";
import { CalendarDayTd } from "./calendar.day.td.component";

@Component({
    selector: "s25-ng-calendar-table",
    standalone: true,
    imports: [CommonModule, CalendarDayTd],
    template: `
        <table class="ngSize100 MonthCalendarTable caltable">
            <caption class="visually-hidden">
                Event Calendar
            </caption>
            <thead>
                @for (weekday of weekdays(); track $index) {
                    <th class="CalendarHeader">
                        {{ weekday }}
                    </th>
                }
            </thead>
            <tbody>
                @for (row of cells2d(); track $index) {
                    <tr>
                        @for (cell of row; track $index) {
                            @let thisMonth = S25Util.date.equalMonth(date(), cell.date);
                            @let today = cell.is_today;
                            <td
                                s25-ng-calendar-day-event
                                class="CalendarCell ngTD ngZmid"
                                [class.CalendarCellToday]="today"
                                [class.ngMonthclass1]="thisMonth && !today"
                                [class.ngMonthclass2]="!thisMonth && !today"
                                [day]="cell"
                            ></td>
                        }
                    </tr>
                }
            </tbody>
        </table>
    `,
    encapsulation: ViewEncapsulation.Emulated,
    styles: `
        :host {
            --calendar-bg: var(--color--surface-bg);
            --calendar-header-bg: var(--color-primary);
            --calendar-header-fg: white;
            --calendar-bg-hover: rgb(28 89 130 / 25%);
            --color--link-text: #0b3a59;
            --color--link-text-hover: var(--color--link-text);
        }
        :host-context(.nm-party--on) :host {
            /* TODO: consider updating --color--surface-bg to #1b1a1e */
            --calendar-bg: #1b1a1e;
            --calendar-header-bg: var(--color-primary-darker);
            --calendar-header-fg: var(--color--text);
            --calendar-bg-hover: rgb(90 119 140 / 40%);
            --color--link-text: #8dbfe8;
            --color--link-text-hover: #b7d7f1;
        }
        .CalendarCell {
            border-width: 1px 1px 1px 1px;
            border-color: var(--color--border);
            width: 14%;
            vertical-align: top;
            border-style: solid;
            cursor: default;
            background-color: var(--calendar-bg);

            @media screen and (max-width: 830px) {
                width: 100%;
            }
        }
        .CalendarCellToday {
            --calendar-header-bg: #1a527f;
            /* soft yellow */
            --calendar-bg: #fffacd;
        }
        .ngMonthclass2 {
            /* very subtle yellow */
            --calendar-bg: #fffff2;
        }
        :host-context(.nm-party--on) {
            .CalendarCellToday {
                --calendar-header-bg: hsl(204 100% 60% / 1);
                --calendar-header-fg: var(--calendar-bg);
                /* soft yellow relative to dark surface */
                --calendar-bg: hsl(41 40% 12% / 1);
            }
            .ngMonthclass2 {
                /* very subtle yellow relative to dark surface */
                --calendar-bg: hsl(60 12% 12% / 1);
            }
        }
        @media print {
            .CalendarCell {
                --calendar-bg: var(--color--surface-bg) !important;
            }
        }
    `,
})
export class CalendarTable {
    readonly events = input.required<CalendarData.DayOfEvents[]>();
    readonly weekdays = input.required<Proto.DowName[]>();
    readonly date = input.required<Date>();
    readonly daysPerRow = input(7, {
        transform: (val) => numberAttribute(val, 7),
    });

    readonly cells2d = computed(() => Array.from(S25Util.array.batch(this.events(), this.daysPerRow())));

    protected readonly S25Util = S25Util;
}
